import { useCallback } from "react";
import * as Sentry from "@sentry/react";
import { getJamParamTypes, getPmmParamTypes, getSignatureData } from "../../utils/orderUtils";
import { OrderStatus } from "../../types/orderStatus";
import { GlobalStore } from "../../store/global/GlobalStore";
import { isBestQuotePmm } from "../../utils/quoteUtils";
import { JamMessage, Message } from "../../types/message";
import { signTypedData } from "wagmi/actions";
import { Hash } from "viem";

const useSignOrder = () => {
  const { quote, network } = GlobalStore.useState(s => {
    return {
      network: s.network,
      quote: s.quote,
    };
  });

  return useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      if (quote === undefined) {
        Sentry.captureException(`Sign order function called with quote being undefined`);
        return;
      }
      const message = quote.toSign;

      try {
        if (message !== undefined) {
          const signPmmTypedData = async () =>
            signTypedData({
              domain: {
                name: "BebopSettlement",
                version: "1",
                //@ts-expect-error Wagmi has inconsistent Chain typing as of wagmi@0.11.7
                chainId: Number(network.id),
                verifyingContract: quote.settlementAddress,
              } as const,
              types: getPmmParamTypes(),
              message: message as Message,
              primaryType: "Aggregate",
            });
          const signJamTypedData = async () =>
            signTypedData({
              domain: {
                name: "JamSettlement",
                version: "1",
                //@ts-expect-error Wagmi has inconsistent Chain typing as of wagmi@0.11.7
                chainId: Number(network.id),
                verifyingContract: quote.settlementAddress,
              } as const,
              types: getJamParamTypes(),
              message: message as JamMessage,
              primaryType: "JamOrder",
            });
          const signTypedDataToUse = isBestQuotePmm(quote.routeType)
            ? signPmmTypedData
            : signJamTypedData;
          signTypedDataToUse()
            .then(signedData => {
              const formattedSignedData = getSignatureData(signedData);
              resolve();
              GlobalStore.update(s => {
                s.orderStatus = OrderStatus.IN_PROGRESS;
                s.signedMessage = {
                  message: message,
                  //@ts-expect-error Wagmi has inconsistent Chain typing as of wagmi@0.11.7
                  chain: network,
                  quote: quote,
                  signature: formattedSignedData.signature as Hash,
                };
              });
            })
            .catch(e => {
              /**
               * An exception is thrown when the user rejects to sign
               * In that case throw them back to Order Review
               */
              console.error(e);
              Sentry.captureException(`signTypedData function failed`, { extra: { error: e } });
              reject();
              GlobalStore.update(s => {
                s.orderStatus = OrderStatus.ENTRY;
                s.signedMessage = undefined;
                s.modalSelector = undefined;
              });
            })
            .finally(() => {
              Sentry.captureMessage(
                `signTypedData function completed - unsetting isSigningOrderRef`
              );
            });
        } else {
          reject();
          Sentry.captureException(`Attempted to sign an order without a quote message`);
        }
      } catch (e) {
        reject();
        console.error(e);
        Sentry.captureException(`signTypedData function failed`, { extra: { error: e } });
        GlobalStore.update(s => {
          s.orderStatus = OrderStatus.ENTRY;
          s.signedMessage = undefined;
          s.modalSelector = undefined;
        });
      } finally {
        Sentry.captureMessage(`signTypedData function completed - unsetting isSigningOrderRef`);
      }
    });
  }, [network, quote]);
};

export default useSignOrder;
